import { Attribute } from '@/types/entity/product';
import { classnames } from '@/utils/classnames/classnames';

interface SpecificationsTableProps {
  specifications: Attribute[];
  className?: string;
}

// EWM-198 — Dan's two-column zebra-striped specs table.
// EWM-275 — added the desktop header row (Dan v0 spec) and mobile
// stacking. Header is hidden on `< sm` since the rows themselves
// reveal the label on top of the value when stacked, so doubling
// up would be redundant. When stacked, the row label adopts the same
// `text-11 uppercase tracking-wide` header treatment so each value
// still reads under a clear caption; at `sm` it reverts to the inline
// `text-14 capitalize` left-column label.
const SpecificationsTable = ({ specifications, className }: SpecificationsTableProps) => {
  return (
    <div className={classnames('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm', className)}>
      {/* EWM-620 — Dan's style guide has no Property/Value heading row on the
          specs table; the rows read directly (on mobile each row already shows
          its label above the value, so a header would double up). */}
      <dl>
        {specifications.map((attribute, index) => {
          const isLast = index === specifications.length - 1;
          const isEven = index % 2 === 0;
          return (
            <div
              key={`${attribute.label || 'spec'}-${index}`}
              className={classnames(
                'flex flex-col gap-1 p-4 sm:flex-row sm:items-start sm:gap-6',
                isEven ? 'bg-gray-50' : 'bg-white',
                { 'border-b border-gray-200': !isLast },
              )}
            >
              <dt className="text-11 sm:text-14 font-semibold tracking-wide text-gray-500 uppercase sm:w-48 sm:shrink-0 sm:tracking-normal sm:text-gray-600 sm:capitalize">
                {attribute.label}
              </dt>
              <dd className="text-14 text-gray-800">{attribute.value}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};

export default SpecificationsTable;
